import CustomPagination from "components/CustomPagination";
import Loading from "components/Loading";
import { useTooltip } from "context/TooltipProvider";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchInstructionsByCRNumberAndIncidentRegNo } from "services/doubtfulService";
import { DoubtfulCase, DoubtfulCaseStatus } from "types";
import { errorHandler } from "utils/helper";

type Props = {
  crimeReferenceNumber?: string;
  incidentRegistrationNumber?: string;
  setSelectedId: React.Dispatch<React.SetStateAction<string | number>>;
  setPageType: React.Dispatch<React.SetStateAction<"list" | "detail">>;
};

const ITEMS_PER_PAGE = 9;

const DoubtfulList: React.FC<Props> = ({
  crimeReferenceNumber,
  incidentRegistrationNumber,
  setSelectedId,
  setPageType,
}) => {
  const { t } = useTranslation();
  const { showTooltip } = useTooltip();

  const [instructions, setInstructions] = useState<DoubtfulCase[]>([]);
  const [loading, setLoading] = useState(true);

  // const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  // const navigate = useNavigate();

  const fetchData = () => {
    setLoading(true);
    fetchInstructionsByCRNumberAndIncidentRegNo(
      crimeReferenceNumber || "",
      incidentRegistrationNumber || "",
    )
      .then((data: DoubtfulCase[]) => {
        setInstructions(data);
        setLoading(false);
      })
      .catch((error) => {
        showTooltip(errorHandler(error));
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, []);

  // const searchTerm = search.trim().toLowerCase();
  // const filtered = instructions.filter((item) => {
  //   const incident_registration_number =
  //     item?.incident_registration_number?.toString()?.toLowerCase() || "";
  //   const crime_registration_number =
  //     item?.crime_registration_number?.toString()?.toLowerCase() || "";
  //   const prosecutor_registration_number =
  //     item?.prosecutor_registration_number?.toString()?.toLowerCase() || "";
  //   const status = item?.request_status?.toString()?.toLowerCase() || "";
  //   const matchesText =
  //     incident_registration_number.includes(searchTerm) ||
  //     crime_registration_number.includes(searchTerm) ||
  //     prosecutor_registration_number.includes(searchTerm) ||
  //     status.includes(searchTerm);
  //   return matchesText;
  // });

  // Pagination logic
  const pageCount = Math.ceil(instructions.length / ITEMS_PER_PAGE);
  const paginated = instructions.slice(
    currentPage * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE + ITEMS_PER_PAGE,
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const getStatusLabel = (status?: string | null) => {
    if (!status) return "";

    const normalizedStatus = status.toLowerCase().replace(/[^a-z0-9]+/g, "_");
    const mappedStatusLabel =
      status in DoubtfulCaseStatus
        ? DoubtfulCaseStatus[status as keyof typeof DoubtfulCaseStatus]
        : undefined;

    const fallback =
      mappedStatusLabel ||
      status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

    return t(`status_${normalizedStatus}`, { defaultValue: fallback });
  };

  return (
    <div className="max-w-screen-xl space-y-6">
      <div className="max-w-screen-xl ml-1 mr-4 space-y-6">
        <div className="overflow-hidden bg-white shadow rounded-xl">
          {/* <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 p-4 border-b">
            <div className="relative w-full md:w-80">
              <span className="absolute inset-y-0 flex items-center text-gray-500 pointer-events-none left-3">
                🔍
              </span>
              <input
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(0);
                }}
                placeholder="Search"
                className="w-full py-2 pl-10 pr-4 text-sm text-black placeholder-gray-600 bg-gray-200 rounded-md focus:outline-none"
              />
            </div>
          </div> */}
          {loading ? (
            <Loading label={t("loading_doubtful_cases")} />
          ) : paginated.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              {t("no_instructions_found")}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-4">
              {paginated.map((item, idx) => (
                <div
                  key={item.id || idx}
                  className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 flex flex-col gap-2 transition-all duration-150 hover:shadow-lg"
                >
                  <div className="flex flex-row justify-between items-center mb-2">
                    <span className="font-semibold text-[#AA232F] text-base">
                      {item.crime_registration_number}
                    </span>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-semibold shadow-sm
                        ${
                          item.request_status === "Pending"
                            ? "bg-yellow-50 text-yellow-800 border border-yellow-200"
                            : item.request_status === "Approved"
                              ? "bg-green-50 text-green-800 border border-green-200"
                              : item.request_status === "Rejected"
                                ? "bg-red-50 text-red-800 border border-red-200"
                                : item.request_status === "Resolved"
                                  ? "bg-blue-50 text-blue-800 border border-blue-200"
                                  : item.request_status ===
                                        "forwarded_to_cordinator" ||
                                      item.request_status ===
                                        "forwarded_to_cordinator_giving"
                                    ? "bg-purple-50 text-purple-800 border border-purple-200"
                                    : item.request_status ===
                                          "assigned_to_team_asking" ||
                                        item.request_status ===
                                          "assigned_to_team_giving"
                                      ? "bg-indigo-50 text-indigo-800 border border-indigo-200"
                                      : item.request_status ===
                                            "forwarded_to_registrar" ||
                                          item.request_status ===
                                            "forwarded_to_registrar_aftergiving"
                                        ? "bg-orange-50 text-orange-800 border border-orange-200"
                                        : item.request_status ===
                                            "Pending_for-giving"
                                          ? "bg-pink-50 text-pink-800 border border-pink-200"
                                          : item.request_status ===
                                              "respond_backto_prosecutor"
                                            ? "bg-gray-100 text-gray-800 border border-gray-300"
                                            : "bg-gray-50 text-gray-700 border border-gray-200"
                        }
                      `}
                    >
                      {getStatusLabel(item.request_status)}
                    </span>
                  </div>
                  <div className="flex flex-col gap-1 mb-2">
                    <span className="text-sm text-gray-700">
                      <span className="font-semibold">
                        {t("incident_reg")}:
                      </span>{" "}
                      {item.incident_registration_number}
                    </span>
                    <span className="text-sm text-gray-700">
                      <span className="font-semibold">
                        {t("doubtful_prosecutor_reg")}:
                      </span>{" "}
                      {item.prosecutor_registration_number}
                    </span>
                    <span className="text-sm text-gray-700">
                      <span className="font-semibold">
                        {t("requested_date")}:
                      </span>{" "}
                      {item.requested_date
                        ? new Date(item.requested_date).toLocaleDateString()
                        : ""}
                    </span>
                  </div>
                  <div className="flex flex-row justify-end gap-2 mt-2">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedId(item?.id || "");
                        setPageType("detail");
                      }}
                      className="px-4 py-1 text-sm bg-[#212121] text-white rounded hover:bg-[#AA232F] transition"
                    >
                      {t("view")}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {/* Pagination */}
          {pageCount > 1 && (
            <div className="flex justify-center my-8">
              <CustomPagination
                pageCount={pageCount}
                currentPage={currentPage}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DoubtfulList;
